"use client";

import { Fragment } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  BarChart3,
  Bot,
  Calculator,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Headphones,
  ListChecks,
  PackageSearch,
  Plug,
  Users,
  Workflow,
  Zap,
} from "lucide-react";
import { StructuredData } from "@/components/common";
import ServiceFaq, {
  type ServiceFaqItem,
} from "@/components/services/ServiceFaq";
import { CTAButton } from "@/components/ui/cta-button";
import { WhatsAppCta } from "@/components/shared/WhatsAppCta";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

// Outcome stats — hairline-divided editorial row
const stats: Array<{ value: string; label: string }> = [
  { value: "60–80%", label: "Less manual work in automated workflows" },
  { value: "24/7", label: "Execution — agents don't keep office hours" },
  { value: "Weeks", label: "From kickoff to a working agent, not months" },
  { value: "0", label: "Systems to replace — agents plug into your stack" },
];

// What an agent actually does — the four-step loop
const agentSteps: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: "Plan",
    description:
      "The agent breaks your goal into concrete steps before anything runs — which records to pull, which systems to touch, in what order.",
    icon: ListChecks,
  },
  {
    title: "Call your systems",
    description:
      "It connects to the tools you already use — ERP, CRM, spreadsheets, email — through their APIs, the same way your team would.",
    icon: Plug,
  },
  {
    title: "Execute",
    description:
      "It does the work: extracts the data, updates the records, drafts the documents, sends the follow-ups — end to end.",
    icon: Zap,
  },
  {
    title: "Report",
    description:
      "You get the finished result plus a full trail of what was done — every step auditable, exceptions flagged for a human.",
    icon: ClipboardCheck,
  },
];

// Worked example — invoice processing, rendered as a run log
const workedExampleSteps: string[] = [
  "New supplier invoice lands in the finance inbox",
  "Agent extracts the amount, PO number, and payment terms",
  "Matches the invoice against the purchase order in your ERP",
  "Posts the entry, schedules the payment, files the PDF",
];

// The three offers
const offers: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
  features: string[];
}> = [
  {
    title: "Custom AI agents",
    description:
      "Agents designed around your exact workflow, built on Claude and modern agentic stacks — they plan, call your tools, and finish multi-step jobs end to end.",
    icon: Bot,
    features: [
      "Workflow mapping and agent design",
      "Built on Claude and agentic stacks",
      "Guardrails and human approval gates",
    ],
  },
  {
    title: "OpenClaw setup & management",
    description:
      "We deploy and manage OpenClaw — the popular open-source AI agent platform — on your own infrastructure, so your agents run with full data sovereignty.",
    icon: Workflow,
    features: [
      "Professional deployment and security hardening",
      "Runs on your infrastructure, your data stays yours",
      "24/7 monitoring and managed operations",
    ],
  },
  {
    title: "Workflow automation & integration",
    description:
      "We wire agents into the tools you already run — ERP, CRM, spreadsheets, email — so work flows across systems without anyone copy-pasting.",
    icon: Plug,
    features: [
      "ERP, CRM, and database integration",
      "Spreadsheet and email automation",
      "API development for legacy systems",
    ],
  },
];

// Where agents pay off first — use cases by function
const useCases: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: "Finance ops",
    description:
      "Invoice matching, payment scheduling, expense checks, and month-end preparation.",
    icon: Calculator,
  },
  {
    title: "Customer operations",
    description:
      "Order status updates, ticket triage, and follow-ups that never slip through.",
    icon: Headphones,
  },
  {
    title: "Document processing",
    description:
      "Extract, classify, and file contracts, forms, and PDFs at volume.",
    icon: FileText,
  },
  {
    title: "Reporting",
    description:
      "Weekly and monthly reports assembled from your systems — on schedule, every time.",
    icon: BarChart3,
  },
  {
    title: "Procurement",
    description:
      "PO creation, quote comparison, supplier follow-ups, and delivery chasing.",
    icon: PackageSearch,
  },
  {
    title: "HR admin",
    description:
      "Onboarding checklists, leave processing, and document collection.",
    icon: Users,
  },
];

// FAQ — questions an operations leader actually asks.
// Stored in English because English doubles as the i18n lookup key. ServiceFaq
// translates internally via t(); the JSON-LD has to be translated at the call
// site with localiseFaqs, or /ms and /zh ship a FAQPage whose questions do not
// match the ones on the page.
const faqs: ServiceFaqItem[] = [
  {
    question: "What does an AI automation agency do that a software company doesn't?",
    answer: "A software house builds what you specify. An AI automation agency has to decide what is worth building, because an AI workflow fails differently — it is probabilistic, it drifts as your data changes, and it needs evaluation rather than only testing. In practice that means workflow design before code, model selection, connecting the agent to the systems you already run, grading it against your real data before go-live, and monitoring accuracy afterwards. If a vendor quotes an AI project the way they would quote a website, that is the warning sign.",
  },
  {
    question: "How do you design an AI workflow?",
    answer: "We map the workflow first: where the judgement steps actually are, which of them a human must still approve, and what the agent does when it is not confident. Only then do we build. Most of the value comes from that mapping — plenty of the workflows businesses bring us turn out to need a scheduled data sync rather than an agent, and we would rather tell you that in week one than month three.",
  },
  {
    question: "What is AI automation, and how is it different from ordinary automation?",
    answer:
      "Ordinary automation follows rules you wrote: if this, then that. It breaks the moment reality deviates from the rule. AI automation handles the judgement steps in between — reading an unstructured invoice, deciding which purchase order it matches, noticing that something does not add up and escalating instead of guessing. For most Malaysian businesses the win is not replacing a process, it is finally automating the messy 20% that rules could never cover.",
  },
  {
    question: "Which processes are worth automating with AI first?",
    answer:
      "The ones that are high-volume, rules-light and currently done by a person reading something. Invoice matching, order intake from WhatsApp or email, support triage, document checks. Start where you can already measure the baseline — how long it takes now, how often it goes wrong — because that is what proves the agent worked. Anything you cannot measure today is a poor first candidate.",
  },
  {
    question: "How much does AI agent development cost in Malaysia?",
    answer:
      "Custom AI agent development in Malaysia is a fixed-scope build cost plus running costs (the Claude model and infrastructure you choose) — we estimate both before any build starts. A first, well-scoped agent typically lands in the low tens of thousands of Ringgit; simpler OpenClaw deployments cost less. You can model the running cost for your team with our Claude cost calculator.",
  },
  {
    question: "What is agentic AI, and how is it different from a chatbot?",
    answer:
      "Agentic AI describes systems that don't just answer — they take action: an agent plans a task, calls tools and APIs, checks its own work, and completes a multi-step workflow end to end. A chatbot replies; an agentic AI does the job. We build agentic AI in Malaysia on Claude using the Claude Agent SDK and the Model Context Protocol (MCP).",
  },
  {
    question: "How long does it take to deploy an AI agent?",
    answer:
      "An OpenClaw deployment is typically operational within 2-3 weeks. Custom agent development takes 4-8 weeks depending on complexity. We always start with one well-scoped workflow, so you see a working agent — and real time savings — early, before expanding to more processes.",
  },
  {
    question: "What systems can agents work with?",
    answer:
      "Anything your team touches today: ERP and accounting systems, CRMs, spreadsheets, email, databases, and internal tools. Agents connect through APIs — and for older systems without modern APIs, we build the integration layer as part of the project.",
  },
  {
    question: "What happens when an agent makes a mistake?",
    answer:
      "Every agent we ship runs with guardrails: results are verified against acceptance criteria, exceptions are routed to a human instead of being pushed through, and high-stakes actions like payments can require explicit approval. You also get a full audit trail of every step the agent took.",
  },
  {
    question: "How is this priced?",
    answer:
      "A fixed-scope build cost for the agent itself, plus running costs that depend on the model and infrastructure you choose — we estimate both before any build starts. Ongoing support and managed operations are optional packages, so you only pay for the level of cover you want.",
  },
  {
    question: "Do we need our own AI team to run this?",
    answer:
      "No. We design, build, deploy, and support the agents — your team's job is to supervise outcomes, not maintain infrastructure. We also train your staff to review the agent's work and adjust its instructions as your processes evolve.",
  },
];

// Translates both fields so the FAQPage JSON-LD matches the visible accordion.
const localiseFaqs = (locale: Locale): ServiceFaqItem[] =>
  faqs.map((faq) => ({
    question: t(locale, faq.question),
    answer: t(locale, faq.answer),
  }));

// Splits a translated headline on the [[...]] accent marker so translators can
// reorder words freely; the marked phrase renders with the emphasis underline.
function renderAccent(text: string) {
  const parts = text.split(/\[\[(.+?)\]\]/g);
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="emphasis-underline">
        {part}
      </span>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

interface AIAgentsAutomationProps {
  locale?: Locale;
}

export default function AIAgentsAutomation({
  locale = defaultLocale,
}: AIAgentsAutomationProps) {
  const prefersReducedMotion = useReducedMotion();

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.55, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  const inView = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, ease: [0.21, 0.6, 0.35, 1] as const },
  };

  return (
    <div className="bg-background">
      {/* FAQ structured data — co-located with the FAQ accordion below */}
      <StructuredData type="faq" faqs={localiseFaqs(locale)} />

      {/* Hero — buyer's pain first, outcome promise second */}
      <section className="bg-background pb-16 pt-20 lg:pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            <div>
              <motion.p
                {...reveal(0)}
                className="mb-5 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
              >
                {t(locale, "AI agent development · agentic AI · Malaysia")}
              </motion.p>
              <motion.h1
                {...reveal(0.08)}
                className="font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl"
              >
                {/* The money term belongs in the H1, not only the title. This page
                    is titled "AI Agent Development Malaysia" while the H1 said
                    "AI automation for the work your team shouldn't be doing" —
                    no "agent", no "Malaysia". Google rewrites titles FROM H1s
                    (proven on /partners/anthropic-claude-partner/), so the title's
                    money term was fighting its own heading. The benefit hook is
                    kept; the term is added in front of it. */}
                {renderAccent(
                  t(
                    locale,
                    "AI agent development for the work your team [[shouldn't be doing]]",
                  ),
                )}
              </motion.h1>
              <motion.p
                {...reveal(0.16)}
                className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-muted md:text-xl"
              >
                {t(
                  locale,
                  "Hours every day go into data entry, copy-paste between systems, report assembly, and follow-ups. We do custom AI agent development in Malaysia — agentic AI built on Claude that takes that work end to end, so your people don't have to.",
                )}
              </motion.p>
              <motion.div
                {...reveal(0.24)}
                className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center"
              >
                <CTAButton
                  href="/contact/"
                  size="large"
                  className="w-full rounded-full sm:w-auto"
                >
                  {t(locale, "Map your first agent workflow")}
                </CTAButton>
                <WhatsAppCta
                  message={"Hi Anchor Sprint, I'd like to talk about building an AI agent (from your AI Agents page)."}
                  location="ai_agents_hero"
                  label={t(locale, "WhatsApp us about an agent")}
                  className="w-full sm:w-auto"
                />
                <Link
                  href="#agent-at-work"
                  className="group inline-flex w-full whitespace-nowrap items-center justify-center gap-2 text-base font-semibold text-neutral-emphasis transition hover:text-primary sm:w-auto"
                >
                  {t(locale, "See an agent at work")}
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden
                  />
                </Link>
              </motion.div>
            </div>

            {/* Editorial artwork — dark proof-moment frame */}
            <motion.div
              {...reveal(0.2)}
              className="overflow-hidden rounded-3xl bg-hero-background p-3 sm:p-4"
            >
              <div className="overflow-hidden rounded-2xl">
                <Image
                  src="/images/services/ai-agent-editorial.jpg"
                  alt={t(
                    locale,
                    "Editorial ink artwork of a request flowing through branching tool calls to a completed checkmark",
                  )}
                  width={768}
                  height={512}
                  priority
                  className="h-auto w-full object-cover"
                />
              </div>
              <p className="px-2 pb-1 pt-4 font-mono text-xs uppercase tracking-[0.25em] text-primary-contrast/50">
                {t(locale, "Request → tool calls → work completed")}
              </p>
            </motion.div>
          </div>
        </div>
        <div className="mt-16 h-px w-full bg-border" aria-hidden />
      </section>

      {/* Outcome stats strip — blue-ruled editorial row */}
      <section className="bg-background pb-20 pt-2 lg:pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map(({ value, label }, index) => (
              <motion.div
                key={label}
                {...inView}
                transition={{
                  duration: 0.5,
                  delay: index * 0.06,
                  ease: [0.21, 0.6, 0.35, 1] as const,
                }}
                className="border-l-2 border-primary/30 pl-5"
              >
                <p className="font-display text-3xl font-bold text-neutral-emphasis">
                  {t(locale, value)}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, label)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Earns the terms this page is assigned but never used. It ranked p25.6 for
          "ai automation agency malaysia" and p24.3 for "ai workflow malaysia" while
          containing neither phrase — which is why /services/ai-solutions/ kept taking
          231 of the cluster's 504 impressions against this page's 71. */}
      <section className="bg-neutral-surface py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "What we are")}
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {renderAccent(
                t(locale, "An AI automation agency, not a [[software house]]"),
              )}
            </h2>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-neutral-muted">
              <p>
                {t(
                  locale,
                  "An AI automation agency designs and runs the AI workflow itself — not just the software around it. A software house builds what you specify. We have to work out what is worth building, because an AI workflow fails differently: it is probabilistic, it drifts as your data changes, and it needs evaluation rather than only testing.",
                )}
              </p>
              <p>
                {t(
                  locale,
                  "That is the difference between AI agent development and ordinary automation work. Rule-based automation breaks the moment reality deviates from the rule you wrote. An AI agent handles the judgement in the middle — reading the unstructured invoice, deciding which purchase order it matches, escalating when something does not reconcile instead of guessing.",
                )}
              </p>
            </div>
          </motion.div>

          <motion.div {...inView} className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              {
                h: "AI workflow design",
                p: "We map the workflow before writing anything — where the judgement steps are, what a human must still approve, and what happens when the agent is unsure.",
              },
              {
                h: "AI agent development",
                p: "Built on Claude, connected to the systems you already run, and graded against your real data before it touches a customer or a ledger.",
              },
              {
                h: "Running it afterwards",
                p: "An AI workflow is not a project you hand over. We monitor accuracy, watch for drift as your data changes, and stay responsible for it.",
              },
            ].map((card) => (
              <div
                key={card.h}
                className="rounded-lg border border-border bg-card p-6"
              >
                <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                  {t(locale, card.h)}
                </h3>
                <p className="mt-2.5 text-base leading-relaxed text-neutral-muted">
                  {t(locale, card.p)}
                  {/* Contextual anchor to the "Claude partner Malaysia" owner
                      page (2026-09-30), on the card that says "Built on Claude". */}
                  {card.h === "AI agent development" && (
                    <>
                      {" "}
                      {t(locale, "We do it as a ")}
                      <Link
                        href={getLocalizedPathname(
                          "/partners/anthropic-claude-partner/",
                          locale,
                        )}
                        className="font-medium text-primary underline underline-offset-4"
                      >
                        {t(locale, "Claude partner in Malaysia")}
                      </Link>
                      {t(locale, ", from Selangor.")}
                    </>
                  )}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What an agent actually does — step walk-through + worked example */}
      <section id="agent-at-work" className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "How it works")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {renderAccent(t(locale, "What an agent [[actually does]]"))}
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Not a chatbot that suggests — an agent that executes. The same loop the artwork above shows, applied to your real workflows.",
              )}
            </p>
          </motion.div>

          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            {/* The four steps — hairline-divided editorial rows */}
            <div className="divide-y divide-border border-y border-border">
              {agentSteps.map(({ title, description, icon: Icon }, index) => (
                <motion.div
                  key={title}
                  {...inView}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.06,
                    ease: [0.21, 0.6, 0.35, 1] as const,
                  }}
                  className="flex gap-5 py-6"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" aria-hidden />
                  </div>
                  <div>
                    <div className="mb-1.5 flex items-baseline gap-3">
                      <span className="font-mono text-sm text-neutral-muted/60">
                        0{index + 1}
                      </span>
                      <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                        {t(locale, title)}
                      </h3>
                    </div>
                    <p className="text-sm leading-relaxed text-neutral-muted">
                      {t(locale, description)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Worked example — dark proof moment */}
            <motion.div
              {...inView}
              className="flex flex-col rounded-3xl bg-hero-background p-8 sm:p-10"
            >
              <p className="mb-8 font-mono text-xs uppercase tracking-[0.25em] text-primary-contrast/50">
                {t(locale, "Worked example — invoice processing")}
              </p>
              <div className="flex-1 space-y-4">
                {workedExampleSteps.map((step, index) => (
                  <div
                    key={step}
                    className="flex items-start gap-4 rounded-2xl border border-primary-contrast/15 p-4"
                  >
                    <span className="font-mono text-xs text-primary">
                      0{index + 1}
                    </span>
                    <p className="font-mono text-sm leading-relaxed text-primary-contrast/80">
                      {t(locale, step)}
                    </p>
                  </div>
                ))}
                <div className="flex items-start gap-3 rounded-2xl border border-primary/50 bg-primary/10 p-4">
                  <CheckCircle2
                    className="h-5 w-5 shrink-0 text-primary"
                    aria-hidden
                  />
                  <p className="text-sm leading-relaxed text-primary-contrast">
                    {t(
                      locale,
                      "Your finance team reviews one flagged exception — not forty invoices.",
                    )}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The offers */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "What we offer")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {renderAccent(
                t(locale, "Three ways to put agents [[to work]]"),
              )}
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Whether you want a bespoke agent, a managed open-source platform, or your existing tools wired together — we build, deploy, and support it.",
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {offers.map(({ title, description, icon: Icon, features }, index) => (
              <motion.div
                key={title}
                {...inView}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                  ease: [0.21, 0.6, 0.35, 1] as const,
                }}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <h3 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                  {t(locale, title)}
                </h3>
                <p className="mb-5 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, description)}
                </p>
                <ul className="mb-6 flex-1 space-y-2">
                  {features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 text-sm text-neutral-muted"
                    >
                      <span
                        className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary"
                        aria-hidden
                      />
                      {t(locale, feature)}
                    </li>
                  ))}
                </ul>
                <Link
                  href={getLocalizedPathname("/contact/", locale)}
                  className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 hover:gap-2.5"
                >
                  {t(locale, "Discuss your workflow")}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Where agents pay off first — use-case grid */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Use cases")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {renderAccent(t(locale, "Where agents [[pay off first]]"))}
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "The fastest returns come from high-volume, rule-heavy work that follows the same steps every time. Start where the hours pile up.",
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={title}
                {...inView}
                transition={{
                  duration: 0.5,
                  delay: index * 0.06,
                  ease: [0.21, 0.6, 0.35, 1] as const,
                }}
                className="flex gap-4 rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <div>
                  <h3 className="mb-1.5 font-display text-base font-semibold text-neutral-emphasis">
                    {t(locale, title)}
                  </h3>
                  <p className="text-sm leading-relaxed text-neutral-muted">
                    {t(locale, description)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Deep links into the use-case directory — the full build per use case */}
          <motion.div
            {...inView}
            className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm"
          >
            <span className="font-semibold text-neutral-emphasis">
              {t(locale, "See the full build:")}
            </span>
            <Link
              href={getLocalizedPathname(
                "/solutions/agent-use-cases/order-processing/",
                locale,
              )}
              className="font-medium text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t(locale, "WhatsApp order processing")}
            </Link>
            <span className="text-neutral-muted" aria-hidden>
              ·
            </span>
            <Link
              href={getLocalizedPathname(
                "/solutions/agent-use-cases/credit-evaluation/",
                locale,
              )}
              className="font-medium text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t(locale, "credit evaluation")}
            </Link>
            <span className="text-neutral-muted" aria-hidden>
              ·
            </span>
            <Link
              href={getLocalizedPathname("/solutions/agent-use-cases/", locale)}
              className="inline-flex items-center gap-1 font-medium text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t(locale, "the full directory")}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </motion.div>

          {/* Cross-links: break the silo — blog (education), calculator (cost), agency hub */}
          <motion.div
            {...inView}
            className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm"
          >
            <span className="font-semibold text-neutral-emphasis">
              {t(locale, "Go deeper:")}
            </span>
            <Link
              href={getLocalizedPathname(
                "/blog/ai-agent-development-malaysia-2026/",
                locale,
              )}
              className="font-medium text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t(locale, "AI agent development guide")}
            </Link>
            <span className="text-neutral-muted" aria-hidden>
              ·
            </span>
            <Link
              href={getLocalizedPathname(
                "/tools/claude-cost-calculator/",
                locale,
              )}
              className="font-medium text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t(locale, "estimate the cost")}
            </Link>
            <span className="text-neutral-muted" aria-hidden>
              ·
            </span>
            <Link
              href={getLocalizedPathname("/services/ai-solutions/", locale)}
              className="font-medium text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t(locale, "our AI agency services")}
            </Link>
            <span className="text-neutral-muted" aria-hidden>
              ·
            </span>
            {/* SG section is English-only — plain /singapore/ href (no locale prefix, would 404) */}
            <Link
              href="/singapore/"
              className="font-medium text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t(locale, "AI agents in Singapore")}
            </Link>
            <span className="text-neutral-muted" aria-hidden>
              ·
            </span>
            <Link
              href={getLocalizedPathname(
                "/blog/openai-dots-chatgpt-agents-malaysia/",
                locale,
              )}
              className="font-medium text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
            >
              {t(locale, "OpenAI dots, the always-on agents in ChatGPT")}
            </Link>
          </motion.div>

          {/* Grant strip — light card, no overclaiming */}
          <motion.div
            {...inView}
            className="mt-10 rounded-2xl border border-border bg-card p-6 sm:p-8"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-3xl text-neutral-muted">
                <strong className="text-neutral-emphasis">
                  {t(locale, "Malaysian business?")}
                </strong>{" "}
                {t(
                  locale,
                  "MDEC initiatives such as the Malaysia Digital Acceleration Grant (MDAG-AI) may help eligible companies offset part of the cost of AI adoption — ask us whether your agent project qualifies.",
                )}{" "}
                {t(locale, "Budget 2027 also adds a RM30 million MDEC fund to help 4,000 MSMEs adopt AI: ")}
                <Link
                  href={getLocalizedPathname("/guides/malaysia-budget-2027/", locale)}
                  className="font-medium text-primary underline underline-offset-4"
                >
                  {t(locale, "see our Budget 2027 summary")}
                </Link>
                {t(locale, ".")}
              </p>
              <Link
                href={getLocalizedPathname("/contact/", locale)}
                className="group inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 hover:gap-2.5"
              >
                {t(locale, "Ask about grant eligibility")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "FAQ")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {renderAccent(t(locale, "Questions ops leaders [[ask us]]"))}
            </h2>
          </motion.div>
          <div className="max-w-4xl">
            <ServiceFaq items={faqs} />
          </div>
        </div>
      </section>

      {/* Closing CTA — dark proof moment */}
      <section className="bg-background pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...inView}
            className="rounded-3xl bg-hero-background p-10 sm:p-14"
          >
            <div className="max-w-3xl">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
                {t(locale, "Start small, start now")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-primary-contrast md:text-4xl">
                {t(locale, "Ready to hand off the busywork?")}
              </h2>
              <p className="mt-4 text-lg text-primary-contrast/70">
                {t(
                  locale,
                  "Tell us the most repetitive workflow on your team and we'll map how an agent would take it over — scope, timeline, and cost, before you commit to anything.",
                )}
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <CTAButton
                  href="/contact/"
                  size="large"
                  className="rounded-full"
                >
                  {t(locale, "Map your first agent workflow")}
                </CTAButton>
                <WhatsAppCta
                  message={"Hi Anchor Sprint, I'd like to talk about building an AI agent (from your AI Agents page)."}
                  location="ai_agents_closing_cta"
                  label={t(locale, "WhatsApp us about an agent")}
                />
                <Link
                  href={getLocalizedPathname(
                    "/anthropic-claude-partner-malaysia-singapore/",
                    locale,
                  )}
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-primary-contrast/20 px-8 py-3 text-sm font-semibold text-primary-contrast transition hover:border-primary-contrast/40 hover:bg-primary-contrast/10"
                >
                  {t(locale, "Why we build on Claude")}
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden
                  />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
